import { FILTER_LABEL_DISPLAY_OVERRIDES, USER_PREFS_SCREEN_ID, DISPATCH_COLUMN_MAP, DISPATCH_STATUS_VALUE_LABELS, DISPATCH_STATUS_DISPLAY_TO_API, PREF_COLUMN_NAME_TO_KEY, FILTER_KEY_TO_PREF_NAME, PREF_FILTER_NAME_TO_KEY, EXCLUDED_DISPATCH_COLUMN_KEYS } from "./emailTriageConstants";

// ─── Label helpers ───────────────────────────────────────────────────────────

// ─── Label helpers ───────────────────────────────────────────────────────────

export const generateLabel = (key) => {
  const base = String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
  return FILTER_LABEL_DISPLAY_OVERRIDES[base] ?? base;
};

const DISPATCH_STATUS_TABLE_LABEL_ALIASES = {
  open: "On Time",
};

function normalizeDispatchStatusKey(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

/** Friendly label for a `dispatch_status` filter / table value. */
export function formatDispatchStatusFilterLabel(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return raw;
  const normalized = normalizeDispatchStatusKey(raw);
  if (DISPATCH_STATUS_TABLE_LABEL_ALIASES[normalized]) {
    return DISPATCH_STATUS_TABLE_LABEL_ALIASES[normalized];
  }
  return DISPATCH_STATUS_VALUE_LABELS[normalized] ?? raw;
}

/** Options for filter dropdowns — maps API values to display labels. */
export function formatEmailTriageFilterOptionsForDisplay(filterKey, options) {
  const list = Array.isArray(options) ? options : [];
  if (filterKey !== "dispatch_status") return list;

  const seen = new Set();
  const result = [];
  list.forEach((value) => {
    const label = formatDispatchStatusFilterLabel(value);
    if (!label || seen.has(label)) return;
    seen.add(label);
    result.push(label);
  });
  return result;
}

export function toDisplayEmailTriageFilterValues(filterKey, values) {
  const list = Array.isArray(values) ? values : [];
  if (filterKey !== "dispatch_status") return list;
  return list.map((value) => formatDispatchStatusFilterLabel(value));
}

export function toApiEmailTriageFilterValues(filterKey, values) {
  const list = Array.isArray(values) ? values : [];
  if (filterKey !== "dispatch_status") return list;
  return list.map((value) => {
    const display = String(value ?? "").trim();
    if (DISPATCH_STATUS_DISPLAY_TO_API[display]) {
      return DISPATCH_STATUS_DISPLAY_TO_API[display];
    }
    const normalized = normalizeDispatchStatusKey(display);
    if (normalized === "open") return "on_time";
    if (DISPATCH_STATUS_VALUE_LABELS[normalized]) return normalized;
    return display;
  });
}

/** Ensure legacy `open` selections are sent as `on_time` to the API. */
export function normalizeDispatchStatusFilterApiValues(values) {
  if (!Array.isArray(values)) return values;
  return toApiEmailTriageFilterValues("dispatch_status", values);
}

export const RMPM_ALERT_FILTER_STORAGE_KEY = "filterData_RMPM Alert";

function normalizeScopeFilterValues(value) {
  const toList = (input) => {
    if (Array.isArray(input)) {
      return input.flatMap((item) => toList(item));
    }
    if (input == null) return [];
    const raw = String(input).trim();
    if (!raw) return [];
    if (raw.startsWith("[") && raw.endsWith("]")) {
      try {
        const parsed = JSON.parse(raw);
        return toList(parsed);
      } catch {
        // Fall through and treat as plain text when not valid JSON.
      }
    }
    return [raw];
  };
  return toList(value).filter(Boolean);
}

/**
 * Persona `scope` label → email-triage filter key.
 * "Site Cluster" → site_cluster, "BG" → bg, "All Access" → null (unrestricted).
 */
export function scopeLabelToFilterKey(scopeLabel) {
  const n = String(scopeLabel ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (!n || n === "all_access") return null;
  if (n === "site_cluster" || n === "sitecluster") return "site_cluster";
  if (n === "bg") return "bg";
  if (
    n === "unit_code" ||
    n === "site_code" ||
    n === "unitcode" ||
    n === "sitecode"
  ) {
    return "unit_code";
  }
  return n;
}

/**
 * Normalize raw UserInfo scope into `{ filterKey, values }`.
 * Accepts `{ scope, scope_value }` or an already-normalized object.
 */
export function normalizeUserScope(raw) {
  if (!raw || typeof raw !== "object") {
    return { filterKey: null, values: [] };
  }

  if ("filterKey" in raw || "values" in raw) {
    const filterKey = raw.filterKey || null;
    const values = normalizeScopeFilterValues(raw.values ?? []);
    if (!filterKey || values.length === 0) {
      return { filterKey: null, values: [] };
    }
    return { filterKey, values };
  }

  const filterKey = scopeLabelToFilterKey(raw.scope);
  const values = normalizeScopeFilterValues(
    raw.scope_value ?? raw.scopeValue ?? [],
  );
  if (!filterKey || values.length === 0) {
    return { filterKey: null, values: [] };
  }
  return { filterKey, values };
}

/**
 * Read persona scope from UserInfo storage.
 * @returns {{ scope: string|null, scope_value: unknown } | null}
 */
export function getUserSiteScopeFromRmpmAlertStorage() {
  try {
    const parsed = getStoredUserInfo();
    if (!parsed) return null;
    const userInfo = parsed?.user_info ?? parsed;
    const scope = userInfo?.scope ?? null;
    const scopeValue = userInfo?.scope_value ?? null;
    if (scope == null && scopeValue == null) return null;
    return { scope, scope_value: scopeValue };
  } catch {
    return null;
  }
}

export function hasUserScope(scope) {
  const { filterKey, values } = normalizeUserScope(scope);
  return Boolean(filterKey && values.length);
}

/** Force the scoped filter key to the user's allowed values in filter payloads. */
export function applyUserScopeToFilters(filters, scope) {
  if (!filters || typeof filters !== "object") return filters ?? {};
  const { filterKey, values } = normalizeUserScope(scope);
  if (!filterKey || values.length === 0) return { ...filters };
  const knownKeys = new Set(Object.keys(filters));
  if (!knownKeys.has(filterKey)) return { ...filters };
  return { ...filters, [filterKey]: [...values] };
}

const EMPTY_SELECTED_FILTERS = {
  po_number: [],
  unit_code: [],
  site_cluster: [],
  vendor_name: [],
  material_code: [],
  material_desc: [],
  dispatch_status: [],
  week: [],
  no_of_day_delayed: [],
  format: [],
  bg: [],
};

export function buildInitialSelectedFilters(scope) {
  return applyUserScopeToFilters({ ...EMPTY_SELECTED_FILTERS }, scope);
}

function normalizePersistedFilterValues(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter((v) => v !== "");
}

/** Load saved multi-select filter values from localStorage (survives page refresh). */
export function loadPersistedSelectedFilters(storageKey, fallback = {}) {
  const base =
    fallback && typeof fallback === "object" && !Array.isArray(fallback)
      ? { ...fallback }
      : {};
  try {
    if (typeof window === "undefined" || !window.localStorage) return base;
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return base;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return base;
    const merged = { ...base };
    Object.entries(parsed).forEach(([key, value]) => {
      merged[key] = normalizePersistedFilterValues(value);
    });
    return merged;
  } catch {
    return base;
  }
}

/** Persist current multi-select filter values to localStorage. */
export function savePersistedSelectedFilters(storageKey, filters) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    if (!filters || typeof filters !== "object" || Array.isArray(filters)) return;
    window.localStorage.setItem(storageKey, JSON.stringify(filters));
  } catch {
    // ignore quota / privacy errors
  }
}

/** Remove persisted filter values (used on manual reset). */
export function clearPersistedSelectedFilters(storageKey) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.removeItem(storageKey);
  } catch {
    // ignore
  }
}

/** Map `/user-prefs` filter `name` to internal `filterOptions` / `selectedFilters` key */
export function resolvePrefFilterNameToKey(name) {
  if (name == null || name === "") return "";
  const raw = String(name).trim();
  const n = raw.replace(/[\s-]+/g, "_");
  if (PREF_FILTER_NAME_TO_KEY[n]) return PREF_FILTER_NAME_TO_KEY[n];
  if (PREF_FILTER_NAME_TO_KEY[raw]) return PREF_FILTER_NAME_TO_KEY[raw];
  return n || raw;
}

// ─── Column preference helpers ───────────────────────────────────────────────

/** Treat API `is_active` as boolean (handles 0/1 and string "true"/"false") */
export function normalizePrefIsActive(isActive) {
  if (isActive == null) return false;
  if (typeof isActive === "boolean") return isActive;
  if (typeof isActive === "number") return isActive !== 0;
  const s = String(isActive).toLowerCase().trim();
  if (s === "false" || s === "0" || s === "no" || s === "") return false;
  if (s === "true" || s === "1" || s === "yes") return true;
  return Boolean(isActive);
}

/** Normalize column/pref names for exclusion checks (camelCase, snake_case, labels). */
function normalizeDispatchColumnKey(name) {
  return String(name || "")
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function isExcludedDispatchColumn(name) {
  return EXCLUDED_DISPATCH_COLUMN_KEYS.has(normalizeDispatchColumnKey(name));
}

/** Map user-pref `name` (snake_case, camelCase, or alias) to DISPATCH_COLUMNS key */
function resolvePrefColumnNameToKey(name) {
  if (name == null || name === "") return "";
  const raw = String(name).trim();
  if (DISPATCH_COLUMN_MAP[raw]) return raw;
  const n = raw.toLowerCase().replace(/[\s-]+/g, "_");
  if (PREF_COLUMN_NAME_TO_KEY[n]) return PREF_COLUMN_NAME_TO_KEY[n];
  if (DISPATCH_COLUMN_MAP[n]) return n;
  if (n.includes("_")) {
    const camel = n.replace(/_([a-z])/g, (_, ch) => ch.toUpperCase());
    if (DISPATCH_COLUMN_MAP[camel]) return camel;
  }
  return raw;
}

/** Initial state before `/user-prefs` loads — no default column catalog */
export function buildDispatchColumnPreferenceList() {
  return [];
}

/** Human-readable label for API-only camelCase keys */
export function humanizeDispatchColumnKey(key) {
  const s = String(key || "");
  return s
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

export function columnDefForDispatchKey(key) {
  if (DISPATCH_COLUMN_MAP[key]) return DISPATCH_COLUMN_MAP[key];
  return {
    key,
    label: humanizeDispatchColumnKey(key),
    sortType: "string",
    sortable: true,
  };
}

/**
 * Table + Customise columns: **only** `/user-prefs` `columns` entries (name, priority, is_active).
 * Labels come from `DISPATCH_COLUMN_MAP` when known, otherwise `humanizeDispatchColumnKey`.
 */
export function mapUserPrefsToColumnList(prefsColumns) {
  if (!Array.isArray(prefsColumns) || prefsColumns.length === 0) {
    return [];
  }
  const rows = [];
  const seen = new Set();
  for (const pref of prefsColumns) {
    const key = resolvePrefColumnNameToKey(pref.name);
    if (!key || seen.has(key)) continue;
    if (isExcludedDispatchColumn(key) || isExcludedDispatchColumn(pref.name)) continue;
    seen.add(key);
    const colDef = columnDefForDispatchKey(key);
    const po = Number(pref.priority);
    rows.push({
      column: key,
      /** Exact `name` from GET `/user-prefs` — POST must send the same string (e.g. camelCase). */
      prefName: pref.name == null ? "" : String(pref.name),
      label: colDef.label,
      visibility: normalizePrefIsActive(pref.is_active),
      DisplayOrder: Number.isFinite(po) ? po : rows.length + 1,
    });
  }
  return [...rows].sort((a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0));
}


export function buildUserPrefsPayload(email, columns, filters, userPrefsFilters, filterOpts) {
  console.log(filters, "filters");
  console.log(filterOpts, "filterOpts");
  console.log(userPrefsFilters, "userPrefsFilters");
  return {
    user_email: email,
    screen_id: USER_PREFS_SCREEN_ID,
    columns: columns
      .filter((c) => c.column !== "srNo")
      .map((c) => ({
        name: c.column,
        priority: c.DisplayOrder,
        is_active: c.visibility,
      })),
    filters: filters.map((c) => {
      const key = Object.keys(filterOpts).find((k) => generateLabel(k) === c.label);
      const existingPref = Array.isArray(userPrefsFilters)
        ? userPrefsFilters.find((f) => resolvePrefFilterNameToKey(f.name) === key)
        : undefined;
      const apiName = (key && FILTER_KEY_TO_PREF_NAME[key]) || key;
      return {
        name: existingPref?.name ?? apiName ?? c.label,
        priority: c.DisplayOrder,
        is_active: c.isFavourite,
      };
    }),
  };
}

// ─── Sorting helpers ─────────────────────────────────────────────────────────

export function sortValueForDispatchColumn(row, col) {
  if (col.sortable === false) {
    return {
      type: "none",
      numericValue: 0,
      textValue: "",
    };
  }

  const value = row[col.key];

  if (col.sortType === "number") {
    const numericValue = Number(value);

    return {
      type: "number",
      numericValue: Number.isFinite(numericValue) ? numericValue : 0,
      textValue: "",
    };
  }

  if (col.sortType === "date") {
    if (value == null || value === "") {
      return {
        type: "date",
        numericValue: 0,
        textValue: "",
      };
    }

    const timestamp = new Date(value).getTime();

    return {
      type: "date",
      numericValue: Number.isNaN(timestamp) ? 0 : timestamp,
      textValue: Number.isNaN(timestamp)
        ? String(value).toLowerCase()
        : "",
    };
  }

  return {
    type: "text",
    numericValue: 0,
    textValue: String(value ?? "").toLowerCase(),
  };
}

export function compareDispatchRows(a, b, col) {
  const valueA = sortValueForDispatchColumn(a, col);
  const valueB = sortValueForDispatchColumn(b, col);

  if (valueA.type === "none" || valueB.type === "none") {
    return 0;
  }

  if (
    valueA.type === valueB.type &&
    (valueA.type === "number" || valueA.type === "date")
  ) {
    const numericComparison =
      valueA.numericValue - valueB.numericValue;

    if (numericComparison !== 0) {
      return numericComparison;
    }
  }

  return valueA.textValue.localeCompare(
    valueB.textValue,
    undefined,
    {
      numeric: true,
      sensitivity: "base",
    }
  );
}

function vendorGroupKey(row) {
  return String(
    row?.vendorName ??
    row?.vendor_name ??
    row?.supplierName ??
    row?.supplier_name ??
    row?.["Supplier name"] ??
    "",
  ).trim() || "—";
}

/** Bucket rows by vendor while preserving within-vendor order from the source list. */
export function buildVendorRowGroups(rows) {
  const groups = [];
  const indexByVendor = new Map();
  for (const row of rows) {
    const vendor = vendorGroupKey(row);
    let idx = indexByVendor.get(vendor);
    if (idx === undefined) {
      idx = groups.length;
      indexByVendor.set(vendor, idx);
      groups.push({ vendor, rows: [] });
    }
    groups[idx].rows.push(row);
  }
  return groups;
}

/**
 * Sort rows for vendor-grouped table view: vendors stay contiguous.
 * Default: vendors A–Z, original row order within each vendor.
 * Column sort: rows sorted inside each vendor; vendor blocks ordered by that column
 * (vendor name column sorts blocks only).
 */
export function sortDispatchRowsForVendorGroupedView(rows, sortConfig, columnMap) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const groups = buildVendorRowGroups(rows);

  const sortVendorsAlphabetically = () => {
    groups.sort((a, b) =>
      a.vendor.localeCompare(b.vendor, undefined, { sensitivity: "base" }),
    );
    return groups.flatMap((g) => g.rows);
  };

  if (!sortConfig?.key) return sortVendorsAlphabetically();

  const col = columnMap[sortConfig.key];
  if (!col || col.sortable === false) return sortVendorsAlphabetically();

  const dir = sortConfig.direction === "asc" ? 1 : -1;

  for (const g of groups) {
    g.rows.sort((a, b) => dir * compareDispatchRows(a, b, col));
  }

  if (sortConfig.key === "vendorName") {
    groups.sort((a, b) =>
      dir * a.vendor.localeCompare(b.vendor, undefined, { sensitivity: "base" }),
    );
  } else {
    groups.sort((a, b) => {
      const ra = a.rows[0];
      const rb = b.rows[0];
      if (!ra || !rb) return 0;
      return dir * compareDispatchRows(ra, rb, col);
    });
  }

  return groups.flatMap((g) => g.rows);
}

// ─── Email triage: dispatch row key + process_mail/v2 `items` ───────────────

/** Stable row key for grouped-table selection (must match checkbox / rowspan logic). */
export function getEmailTriageDispatchRowKey(row) {
  if (!row || typeof row !== "object") return "";
  return `${row.vendorName || ""}|${row.poNumber || ""}|${row.materialCode || ""}|${row.srNo ?? ""}`;
}

/** Coerce dispatch row / selected PO id for `process_mail/v2` `items[].id`. */
export function pickProcessMailItemId(rowOrId) {
  const raw =
    rowOrId != null && typeof rowOrId === "object"
      ? rowOrId.id ?? rowOrId.row_id ?? rowOrId.rowId
      : rowOrId;
  if (raw == null || raw === "") return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function hasGroupedSelection({
  vendorGrouped,
  selectedDispatchRowKeys,
  dispatchRows,
}) {
  return (
    vendorGrouped &&
    selectedDispatchRowKeys &&
    typeof selectedDispatchRowKeys.has === "function" &&
    selectedDispatchRowKeys.size > 0 &&
    Array.isArray(dispatchRows) &&
    dispatchRows.length > 0
  );
}

function isVendorMatch(row, vendor) {
  const rowVendor = String(
    row.vendorName ?? "",
  ).trim();

  if (!vendor || !rowVendor) {
    return true;
  }

  return (
    rowVendor.localeCompare(
      vendor,
      undefined,
      { sensitivity: "accent" },
    ) === 0
  );
}

function buildProcessMailItem(row, vendor) {
  const po =
    row.poNumber != null
      ? String(row.poNumber).trim()
      : "";

  const code =
    row.materialCode != null
      ? String(row.materialCode).trim()
      : "";

  if (!po || !code) {
    return null;
  }

  return {
    id: pickProcessMailItemId(row),
    po_number: po,
    item_code: code,
    vendor_name:
      String(
        row.vendorName ?? vendor,
      ).trim() || vendor,
  };
}

function buildSelectedProcessMailItems({
  dispatchRows,
  selectedDispatchRowKeys,
  getDispatchRowKey,
  vendor,
}) {
  return dispatchRows
    .filter((row) =>
      selectedDispatchRowKeys.has(
        getDispatchRowKey(row),
      ),
    )
    .filter((row) =>
      isVendorMatch(row, vendor),
    )
    .map((row) =>
      buildProcessMailItem(row, vendor),
    )
    .filter(Boolean);
}

function buildFallbackProcessMailItem({
  vendor,
  fallbackPo,
  fallbackMaterialId,
  fallbackId,
}) {
  const po =
    fallbackPo != null
      ? String(fallbackPo).trim()
      : "";

  const code =
    fallbackMaterialId != null
      ? String(fallbackMaterialId).trim()
      : "";

  if (!po || !code) {
    return [];
  }

  return [
    {
      id: pickProcessMailItemId(
        fallbackId,
      ),
      po_number: po,
      item_code: code,
      vendor_name: vendor,
    },
  ];
}

/**
 * Builds `items` for POST `/api/v1/process_mail/v2` from grouped selection + vendor scope,
 * or from fallback PO / material when nothing is selected.
 */
export function buildProcessMailV2ItemsSnapshot({
  vendorScopeName,
  vendorGrouped,
  selectedDispatchRowKeys,
  dispatchRows,
  getDispatchRowKey = getEmailTriageDispatchRowKey,
  fallbackPo,
  fallbackMaterialId,
  fallbackId,
}) {
  const vendor = String(
    vendorScopeName ?? "",
  ).trim();

  if (
    hasGroupedSelection({
      vendorGrouped,
      selectedDispatchRowKeys,
      dispatchRows,
    })
  ) {
    const items =
      buildSelectedProcessMailItems({
        dispatchRows,
        selectedDispatchRowKeys,
        getDispatchRowKey,
        vendor,
      });

    if (items.length > 0) {
      return items;
    }
  }

  return buildFallbackProcessMailItem({
    vendor,
    fallbackPo,
    fallbackMaterialId,
    fallbackId,
  });
}

/** Builds `items` for process_mail/v2 from an explicit list of dispatch rows. */
export function buildProcessMailV2ItemsFromRows(rows, vendorScopeName = "") {
  const vendor = String(vendorScopeName ?? "").trim();
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const out = [];
  for (const row of rows) {
    const po = row.poNumber != null ? String(row.poNumber).trim() : "";
    const code = row.materialCode != null ? String(row.materialCode).trim() : "";
    if (!po || !code) continue;
    out.push({
      id: pickProcessMailItemId(row),
      po_number: po,
      item_code: code,
      vendor_name: String(row.vendorName ?? vendor).trim() || vendor,
    });
  }
  return out;
}

// ─── Email history / draft helpers ───────────────────────────────────────────

/** Internal tag on rows from `email_history` bucketed response (`drafts` / `sent_mails` / `inbox`). */
export const EMAIL_HISTORY_MAILBOX = {
  DRAFTS: "drafts",
  SENT: "sent_mails",
  INBOX: "inbox",
};

export function isDraftEmail(email) {
  if (!email || typeof email !== "object") return false;
  const raw = email.is_draft ?? email.isDraft;
  if (raw === true || raw === 1) return true;
  if (typeof raw === "string") {
    const s = raw.trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes";
  }
  return false;
}

/**
 * Which mailbox tab a row belongs to. Legacy flat lists have no `__mailbox`;
 * drafts are inferred from `is_draft`, everything else is treated as Sent (Inbox stayed empty).
 */
export function getHistoryItemMailbox(email) {
  if (!email || typeof email !== "object") return EMAIL_HISTORY_MAILBOX.SENT;
  const m = email.__mailbox;
  if (m === EMAIL_HISTORY_MAILBOX.DRAFTS || m === EMAIL_HISTORY_MAILBOX.SENT || m === EMAIL_HISTORY_MAILBOX.INBOX) {
    return m;
  }
  return isDraftEmail(email) ? EMAIL_HISTORY_MAILBOX.DRAFTS : EMAIL_HISTORY_MAILBOX.SENT;
}

/**
 * Inbox `mail_content` is `from+to+subject+bodyHtml` (first three `+` delimiters only;
 * body may contain `+` characters).
 */
export function parseInboxMailContent(mailContent) {
  if (mailContent == null || mailContent === "") {
    return { from: "", to: "", subject: "", bodyHtml: "" };
  }
  const s = String(mailContent);
  const p1 = s.indexOf("+");
  if (p1 < 0) {
    return { from: "", to: "", subject: "", bodyHtml: s };
  }
  const from = s.slice(0, p1).trim();
  const rest1 = s.slice(p1 + 1);
  const p2 = rest1.indexOf("+");
  if (p2 < 0) {
    return { from, to: rest1.trim(), subject: "", bodyHtml: "" };
  }
  const to = rest1.slice(0, p2).trim();
  const rest2 = rest1.slice(p2 + 1);
  const p3 = rest2.indexOf("+");
  if (p3 < 0) {
    return { from, to, subject: rest2.trim(), bodyHtml: "" };
  }
  const subject = rest2.slice(0, p3).trim();
  const bodyHtml = rest2.slice(p3 + 1);
  return { from, to, subject, bodyHtml };
}

/** Map inbox API row (`mail_id`, `mail_subject`, `mail_content`, …) to composer/history fields. */
export function mapInboxHistoryRow(row) {
  if (!row || typeof row !== "object") return row;
  const parsed =
    row.mail_content != null && String(row.mail_content).trim() !== ""
      ? parseInboxMailContent(row.mail_content)
      : { from: "", to: "", subject: "", bodyHtml: "" };

  const subject = String(row.mail_subject ?? parsed.subject ?? "").trim();
  const bodyFromField =
    row.email_body != null && String(row.email_body).trim() !== ""
      ? String(row.email_body)
      : parsed.bodyHtml;

  const toList = normalizeRecipientList(row.email_to);
  const parsedTo = parsed.to ? normalizeRecipientList(parsed.to) : [];

  return {
    ...row,
    email_id: row.email_id ?? row.mail_id ?? null,
    mail_id: row.mail_id ?? row.email_id ?? null,
    message_id: pickNullableString(row.message_id ?? row.messageId),
    email_sub: subject || "(No subject)",
    mail_subject: row.mail_subject ?? subject,
    email_body: bodyFromField,
    mail_content: row.mail_content,
    email_from: (row.email_from != null && String(row.email_from).trim() !== ""
      ? String(row.email_from).trim()
      : parsed.from) || "",
    email_to: toList.length > 0 ? toList : parsedTo,
    timestamp: row.timestamp ?? row.mail_timestamp ?? null,
  };
}

function extractLegacyEmailHistory(data) {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.items) && data.items.length > 0) {
    return data.items;
  }

  if (Array.isArray(data?.history) && data.history.length > 0) {
    return data.history;
  }

  return null;
}

function isBucketedEmailHistoryResponse(data) {
  return Boolean(
    data &&
    typeof data === "object" &&
    (
      Array.isArray(data.drafts) ||
      Array.isArray(data.sent_mails) ||
      Array.isArray(data.inbox)
    ),
  );
}

function mapDraftHistoryRow(row) {
  return {
    ...row,
    __mailbox: EMAIL_HISTORY_MAILBOX.DRAFTS,
    is_draft: true,
  };
}

function mapSentHistoryRow(row) {
  return {
    ...row,
    __mailbox: EMAIL_HISTORY_MAILBOX.SENT,
    is_draft: false,
  };
}

function mapInboxMailboxRow(row) {
  return {
    ...mapInboxHistoryRow(row),
    __mailbox: EMAIL_HISTORY_MAILBOX.INBOX,
    is_draft: false,
  };
}

function normalizeBucketedEmailHistory(data) {
  const drafts = Array.isArray(data.drafts)
    ? data.drafts
    : [];

  const sentMails = Array.isArray(data.sent_mails)
    ? data.sent_mails
    : [];

  const inbox = Array.isArray(data.inbox)
    ? data.inbox
    : [];

  return [
    ...drafts.map(mapDraftHistoryRow),
    ...sentMails.map(mapSentHistoryRow),
    ...inbox.map(mapInboxMailboxRow),
  ];
}

export function normalizeEmailHistoryResponse(data) {
  if (!data) {
    return [];
  }

  const legacyRows = extractLegacyEmailHistory(data);

  if (legacyRows) {
    return legacyRows;
  }

  if (isBucketedEmailHistoryResponse(data)) {
    return normalizeBucketedEmailHistory(data);
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  if (Array.isArray(data?.history)) {
    return data.history;
  }

  return [];
}

export function normalizeRecipientList(val) {
  if (Array.isArray(val)) return val.filter(Boolean).map(String);
  if (val == null || val === "") return [];
  return String(val).split(",").map((s) => s.trim()).filter(Boolean);
}

function escapeHtmlForEmailBody(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Plain-text email body: turns `**phrase**` into `<strong>phrase</strong>` (inner text escaped),
 * then newlines → `<br>` so the composer can render as HTML safely.
 */
export function formatPlainEmailBodyWithMarkdownBold(raw) {
  const s = String(raw ?? "");
  const lines = s.split("\n");
  const htmlLines = lines.map((line) => {
    if (!line.includes("**")) return escapeHtmlForEmailBody(line);
    return line
      .split(/(\*\*[^*]+\*\*)/g)
      .map((segment) => {
        const m = segment.match(/^\*\*([^*]+)\*\*$/);
        if (m) return `<strong>${escapeHtmlForEmailBody(m[1])}</strong>`;
        return escapeHtmlForEmailBody(segment);
      })
      .join("");
  });
  return htmlLines.join("<br>");
}

export function normalizeEmailBodyForComposer(raw) {
  if (raw == null) return "";
  const s = String(raw);
  if (!s.trim()) return "";
  const lower = s.trim().toLowerCase();
  if (lower.includes("<html") || lower.includes("<!doctype")) {
    try {
      const doc = new DOMParser().parseFromString(s, "text/html");
      return doc.body ? doc.body.innerHTML : s;
    } catch {
      return s;
    }
  }
  /** Already HTML fragments — do not escape or inject `<br>`. */
  if (/<[a-z][\s\S]*>/i.test(s.trim())) {
    return s;
  }
  if (s.includes("**")) {
    return formatPlainEmailBodyWithMarkdownBold(s);
  }
  return s;
}

export function resolveEmailBodyFromApi(data) {
  if (!data || typeof data !== "object") return "";
  let raw = data.email_body;
  if (raw == null || String(raw).trim() === "") {
    const c = data.content;
    if (c && typeof c === "object" && c.bodyHtml != null) {
      raw = c.bodyHtml;
    } else if (data.bodyHtml != null) {
      raw = data.bodyHtml;
    } else if (data.mail_content != null && String(data.mail_content).trim() !== "") {
      raw = parseInboxMailContent(data.mail_content).bodyHtml;
    }
  }
  return normalizeEmailBodyForComposer(raw == null ? "" : String(raw));
}

export function getEmailRecordId(email) {
  if (!email || typeof email !== "object") return null;
  const id = email.email_id ?? email.emailId ?? email.mail_id ?? email.id;
  if (id === null || id === undefined || id === "") return null;
  return id;
}

function pickNullableString(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
}

function looksLikeHtmlString(s) {
  return /<[a-z][\s\S]*>/i.test(String(s ?? "").trim());
}

function normalizeEmailBodyString(s) {
  if (s == null) return "";
  if (typeof s !== "string") return String(s);
  return s
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n");
}

function htmlToPlainText(html) {
  const s = String(html ?? "");
  if (!s.trim()) return "";
  if (typeof document === "undefined") {
    return s.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
  const el = document.createElement("div");
  el.innerHTML = s;
  return (el.textContent || "").replace(/\u00a0/g, " ").trim();
}

const MAIL_ACTION_BODY_P_STYLE =
  "font-family:Segoe UI,Arial,sans-serif;font-size:14px;";
const MAIL_ACTION_TABLE_STYLE =
  "border-collapse:collapse;font-family:Segoe UI,Arial,sans-serif;font-size:13px;width:100%;max-width:900px;";
const MAIL_ACTION_TH_STYLE =
  "border:1px solid #cccccc;padding:6px 8px;background-color:#f2f2f2;font-weight:600;text-align:left;";
const MAIL_ACTION_TD_STYLE =
  "border:1px solid #cccccc;padding:6px 8px;text-align:left;vertical-align:top;";

function mergeInlineStyle(el, style) {
  if (!el || !style) return;
  const cur = (el.getAttribute("style") || "").trim();
  if (!cur) el.setAttribute("style", style);
}

function plainTextToMailActionHtml(text) {
  const normalized = String(text ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");
  if (!normalized.trim()) return "";
  return normalized
    .split("\n")
    .map((line) => {
      const inner = escapeHtmlForEmailBody(line);
      return inner === ""
        ? `<p style="${MAIL_ACTION_BODY_P_STYLE}"><br></p>`
        : `<p style="${MAIL_ACTION_BODY_P_STYLE}">${inner}</p>`;
    })
    .join("\n");
}

/**
 * Apply Outlook-friendly inline styles to composer/API HTML before POST `/mail_action`.
 */
export function applyMailActionHtmlStyles(html) {
  const s = String(html ?? "").trim();
  if (!s) return "";
  if (typeof document === "undefined") return s;

  const root = document.createElement("div");
  root.innerHTML = s;

  root.querySelectorAll("p").forEach((el) => mergeInlineStyle(el, MAIL_ACTION_BODY_P_STYLE));
  root.querySelectorAll("li").forEach((el) => mergeInlineStyle(el, MAIL_ACTION_BODY_P_STYLE));

  root.querySelectorAll("table").forEach((table) => {
    if (!table.getAttribute("border")) table.setAttribute("border", "1");
    if (!table.getAttribute("cellpadding")) table.setAttribute("cellpadding", "4");
    if (!table.getAttribute("cellspacing")) table.setAttribute("cellspacing", "0");
    mergeInlineStyle(table, MAIL_ACTION_TABLE_STYLE);
  });

  root.querySelectorAll("th").forEach((el) => mergeInlineStyle(el, MAIL_ACTION_TH_STYLE));
  root.querySelectorAll("td").forEach((el) => mergeInlineStyle(el, MAIL_ACTION_TD_STYLE));

  return root.innerHTML;
}

/**
 * Normalize composer content to HTML for send / save-draft (`email_body` on `/mail_action`).
 */
export function formatEmailBodyForMailAction(raw) {
  const normalized = normalizeEmailBodyString(raw);
  if (!normalized.trim()) return "";
  const html = looksLikeHtmlString(normalized)
    ? normalized.trim()
    : plainTextToMailActionHtml(normalized);
  const styled = applyMailActionHtmlStyles(html);
  return htmlToPlainText(styled) ? styled : "";
}

/** Integer id for mail_action, or null if missing / invalid. */
export function pickNullableMailActionEmailId(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Merge last `process_mail/v2` (or similar) payload with the selected `email_history` row.
 * Later keys win so the active history row overrides stale ref fields when both exist.
 */
export function mergeEmailMailSourceRecords(refRecord, selectedRow) {
  const a = refRecord && typeof refRecord === "object" ? { ...refRecord } : {};
  const b = selectedRow && typeof selectedRow === "object" ? { ...selectedRow } : {};
  return normalizeMailActionSourceRecord({ ...a, ...b });
}

/**
 * Coerce empty `message_id` / `messageId` to null and default `is_new_mail` for saved drafts.
 */
export function normalizeMailActionSourceRecord(record) {
  if (!record || typeof record !== "object") return {};
  const out = { ...record };

  const message_id = pickNullableString(out.message_id ?? out.messageId);
  out.message_id = message_id;
  out.messageId = message_id;

  const email_id = pickNullableMailActionEmailId(
    out.email_id ?? out.emailId ?? out.id,
  );

  const explicitNew = out.is_new_mail ?? out.isNewMail;
  if (explicitNew !== true && explicitNew !== false && email_id != null) {
    out.is_new_mail = false;
    out.isNewMail = false;
  }

  return out;
}

/**
 * POST `/api/v1/mail_action` body. Values not present on merged API records + composer
 * use `null` (booleans `is_draft` and `reply_all` stay proper booleans).
 */
export function buildMailActionPayload({
  isDraft,
  emailSubject,
  emailBody,
  emailTo,
  emailCc,
  mergedSource,
}) {
  const src = normalizeMailActionSourceRecord(mergedSource);

  const email_id = pickNullableMailActionEmailId(
    src.email_id ?? src.emailId ?? src.id,
  );

  const thread_id = pickNullableString(src.thread_id ?? src.threadId);
  const message_id = pickNullableString(src.message_id ?? src.messageId);

  const explicitNew = src.is_new_mail ?? src.isNewMail;
  const explicitReplyAll = src.reply_all ?? src.replyAll;

  const is_draft = Boolean(isDraft);

  let is_new_mail;

  if (typeof explicitNew === "boolean") {
    is_new_mail = explicitNew;
  } else if (email_id != null) {
    is_new_mail = false;
  } else {
    is_new_mail = message_id == null;
  }

  if (is_draft) {
    is_new_mail = true;
  }

  const reply_all =
    explicitReplyAll === true || explicitReplyAll === false
      ? Boolean(explicitReplyAll)
      : false;

  const to = normalizeRecipientList(emailTo);
  const cc = normalizeRecipientList(emailCc);

  const sub = emailSubject != null ? String(emailSubject).trim() : "";
  const body = formatEmailBodyForMailAction(emailBody);

  const user_name = pickNullableString(src.user_name ?? src.userName);

  return {
    email_id,
    is_draft,
    email_sub: sub === "" ? null : sub,
    email_body: body === "" ? null : body,
    email_to: to.length > 0 ? to : null,
    email_cc: cc.length > 0 ? cc : null,
    is_new_mail,
    thread_id,
    message_id,
    reply_all,
    user_name,
  };
}

export function pickLatestDraftHistoryIndex(history, draftIndices) {
  if (!draftIndices.length) return null;
  let bestIdx = draftIndices[0];
  let bestTime = -Infinity;
  for (const i of draftIndices) {
    const t = new Date(history[i]?.timestamp || 0).getTime();
    if (!Number.isNaN(t) && t >= bestTime) {
      bestTime = t;
      bestIdx = i;
    }
  }
  return bestIdx;
}

export const EMAIL_TRIAGE_DISPLAY_TIMEZONE = "Asia/Kolkata";

/**
 * Display email history timestamps in Indian Standard Time (IST).
 * Example: "Apr 28, 04:15 PM". Returns raw string when parsing fails.
 */
export function formatEmailTimestampToIST(ts) {
  if (ts == null || ts === "") return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  const opts = { timeZone: EMAIL_TRIAGE_DISPLAY_TIMEZONE };
  const month = d.toLocaleString("en-US", { ...opts, month: "short" });
  const day = d.toLocaleString("en-US", { ...opts, day: "numeric" });
  const time = d.toLocaleString("en-US", {
    ...opts,
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${month} ${day}, ${time}`;
}

export function historyRowTimestampMs(row) {
  const t = new Date(row?.timestamp ?? row?.email?.timestamp ?? 0).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function sortHistoryRowsLatestFirst(rows) {
  return [...rows].sort((a, b) => historyRowTimestampMs(b) - historyRowTimestampMs(a));
}

// ─── User info helpers ───────────────────────────────────────────────────────

export function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("UserInfo");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function getUserEmailFromStorage() {
  const parsed = getStoredUserInfo();
  if (!parsed) return "";
  return String(parsed?.user_info?.email ?? parsed?.email ?? "").trim();
}

/** Reviewer email for TnT save-action payloads (`reviewer_id`). */
export function getReviewerIdFromStorage() {
  return getUserEmailFromStorage();
}

export function getUserNameFromStorage() {
  const parsed = getStoredUserInfo();
  if (!parsed) return "";
  const ui = parsed?.user_info;
  return String(
    ui?.name ?? ui?.user_name ?? parsed?.name ?? parsed?.user_name ?? "",
  ).trim();
}

export function pickUserNameFromHistory(list) {
  if (!Array.isArray(list) || list.length === 0) return "";
  const sorted = [...list].sort(
    (a, b) =>
      new Date(b?.timestamp || 0).getTime() -
      new Date(a?.timestamp || 0).getTime(),
  );
  for (const row of sorted) {
    const un = row?.user_name;
    if (un != null && String(un).trim() !== "") return String(un).trim();
  }
  return "";
}

export function resolveUserNameForEmailApis(historyList, preferredEmailRow) {
  const userName = String(
    preferredEmailRow?.user_name ?? "",
  ).trim();

  if (userName) {
    return userName;
  }

  return pickUserNameFromHistory(historyList) || getUserNameFromStorage();
}

export function getDraftIndices(history) {
  return history
    .map((e, i) =>
      (isDraftEmail(e) || e?.__mailbox === EMAIL_HISTORY_MAILBOX.DRAFTS) ? i : -1,
    )
    .filter((i) => i >= 0);
}

export function selectLatestDraft(history, draftIndices, setIndex, applyComposer) {
  if (draftIndices.length === 1) {
    setIndex(draftIndices[0]);
    applyComposer(history[draftIndices[0]]);
    return;
  }
  const latestIdx = pickLatestDraftHistoryIndex(history, draftIndices);
  setIndex(latestIdx);
  applyComposer(history[latestIdx]);
}

// ─── Email triage dispatch row → RMPM AlertInsight details ─────────────────

function pickDispatchField(row, ...keys) {
  if (!row || typeof row !== "object") return undefined;
  for (const key of keys) {
    const v = row[key];
    if (v != null && String(v).trim() !== "") return v;
  }
  return undefined;
}

function mapDispatchStatusToAlertStatus(dispatchStatus) {
  const s = String(dispatchStatus ?? "").trim().toLowerCase();
  if (!s) return "Open";
  if (s === "accepted" || s === "closed") return "Accepted";
  if (s === "rejected") return "Rejected";
  return "Open";
}

function defaultRmpmResolveData(qtyAtRiskLabel) {
  return [
    {
      Title: "Other Recommendation",
      Info: "",
      isRecommended: "",
      data: qtyAtRiskLabel
        ? [{ label: "Quantity At Risk", value: qtyAtRiskLabel }]
        : [],
    },
  ];
}

/**
 * Map an email-triage dispatch table row to the RMPM `alertDetails` shape expected
 * by `AlertInsight` / `AgenticInsights` (same keys as the alerts grid).
 */
export function mapDispatchRowToRmpmAlertDetails(row) {
  if (!row || typeof row !== "object") return {};
  console.log('row', row);


  if (row["Alert ID"] != null && String(row["Alert ID"]).trim() !== "") {
    return {
      ...row,
      "Alert ID": String(row["Alert ID"]).trim(),
      "Site code": pickDispatchField(row, "Site code", "unitCode", "unit_code") ?? row["Site code"],
      "Site cluster": pickDispatchField(row, "Site cluster", "siteCluster", "site_cluster") ?? row["Site cluster"],
      "Material Id":
        row["Material Id"] ??
        pickDispatchField(row, "materialCode", "material_code", "materialId"),
      "Material description":
        pickDispatchField(row, "Material description", "materialDescription", "material_description") ??
        row["Material description"],
      "Week": Number(row.week),
      resolveData: Array.isArray(row.resolveData) ? row.resolveData : defaultRmpmResolveData(row["Qty at risk"]),
    };
  }

  const uom =
    pickDispatchField(row, "uom", "UOM", "Base Unit of Measure") ?? "EA";
  const qtyRaw =
    pickDispatchField(row, "qtyPlanned", "qty_planned", "qtyAvailable", "qty_available", "Quantity at risk") ??
    null;
  const qtyNum = Number(qtyRaw);
  let qtyAtRisk = "";

  if (qtyRaw != null && qtyRaw !== "") {
    const formattedQty = Number.isFinite(qtyNum)
      ? qtyNum.toLocaleString()
      : qtyRaw;

    qtyAtRisk = `${formattedQty} ${uom}`.trim();
  }
  const alertWeekRaw = pickDispatchField(row, "alertWeek", "alert_week", "week") ?? "";
  const weekDigits = String(alertWeekRaw).replace(/\D/g, "");
  const weekLabel = weekDigits ? `Week ${weekDigits}` : String(alertWeekRaw);

  const materialRaw = pickDispatchField(row, "materialCode", "material_code", "materialId") ?? "";
  const materialNum = Number(materialRaw);

  const dispatchStatus = pickDispatchField(row, "dispatchStatus", "dispatch_status");
  const rootCause = pickDispatchField(row, "rca", "Root Cause", "rootCause") ?? "";

  return {
    "Alert ID": String(pickDispatchField(row, "alertId", "alert_id") ?? ""),
    "Site cluster": String(pickDispatchField(row, "siteCluster", "site_cluster") ?? ""),
    "Site code": String(pickDispatchField(row, "unitCode", "unit_code") ?? ""),
    "Material Id": Number.isFinite(materialNum) ? materialNum : materialRaw,
    "Material description": String(
      pickDispatchField(row, "materialDescription", "material_description") ?? "",
    ),
    "Qty at risk": qtyAtRisk,
    "Quantity at risk": Number.isFinite(qtyNum) ? qtyNum : qtyRaw,
    "Alert status": mapDispatchStatusToAlertStatus(dispatchStatus),
    "Days to Impact": pickDispatchField(row, "noOfDayDelayed", "no_of_day_delayed", "Days to Impact") ?? null,
    "Action Week": weekDigits ? Number(weekDigits) : null,
    "Material type": String(pickDispatchField(row, "materialType", "material_type", "Material type") ?? ""),
    Week: weekLabel,
    CBU: String(pickDispatchField(row, "cbu", "CBU") ?? ""),
    Recommendations: String(pickDispatchField(row, "Recommendations", "recommendations") ?? "Yes"),
    "JIT/Non JIT": String(pickDispatchField(row, "JIT/Non JIT", "jitNonJit") ?? "Non-JIT"),
    resolveData: Array.isArray(row.resolveData) ? row.resolveData : defaultRmpmResolveData(qtyAtRisk),
    Recommendation: String(pickDispatchField(row, "Recommendation", "recommendation") ?? "Other Recommendation"),
    "CBU description": String(pickDispatchField(row, "cbuDescription", "CBU description") ?? ""),
    Basepack: String(pickDispatchField(row, "basepack", "Basepack") ?? ""),
    "Basepack description": String(pickDispatchField(row, "basepackDescription", "Basepack description") ?? ""),
    "Small C": String(pickDispatchField(row, "smallC", "Small C") ?? ""),
    BG: String(pickDispatchField(row, "bg", "BG") ?? ""),
    Format: String(pickDispatchField(row, "format", "Format") ?? ""),
    "Sub format": String(pickDispatchField(row, "subFormat", "Sub format") ?? ""),
    Brand: String(pickDispatchField(row, "brand", "Brand") ?? ""),
    Segmentation: String(pickDispatchField(row, "segmentation", "Segmentation") ?? ""),
    ABCD: String(pickDispatchField(row, "abcd", "ABCD") ?? ""),
    "Supplier Id": String(pickDispatchField(row, "supplierId", "Supplier Id") ?? ""),
    "Supplier name": String(
      pickDispatchField(row, "vendorName", "vendor_name", "Supplier name") ?? "",
    ),
    "Plan Variation": row["Plan Variation"] ?? null,
    PO_Issue: row.PO_Issue ?? null,
    "Supplier Issue": row["Supplier Issue"] ?? null,
    Other: row.Other ?? null,
    "Base Unit of Measure": uom,
    "Created Date": String(pickDispatchField(row, "createdDate", "Created Date") ?? ""),
    "Bottleneck RM": String(pickDispatchField(row, "bottleneckRm", "Bottleneck RM") ?? ""),
    Buyers: String(pickDispatchField(row, "buyers", "Buyers") ?? ""),
    RecommendationOld: String(
      pickDispatchField(row, "RecommendationOld", "recommendationOld") ?? "Other Recommendation",
    ),
    RecommendationNew: String(pickDispatchField(row, "RecommendationNew", "recommendationNew") ?? ""),
    "Impacted CBU Count": pickDispatchField(row, "impactedCbuCount", "Impacted CBU Count") ?? null,
    "Impacted CBU QTY(TO)": pickDispatchField(row, "impactedCbuQty", "Impacted CBU QTY(TO)") ?? null,
    "MAI% (Total)": String(pickDispatchField(row, "maiPercent", "MAI% (Total)") ?? ""),
    "Root Cause": rootCause,
    Demand: String(pickDispatchField(row, "demand", "Demand") ?? ""),
    "Lead Time(Min-Max)": String(
      pickDispatchField(row, "transportationLt", "transportation_lt", "Lead Time(Min-Max)") ?? "",
    ),
    BuyerIntervention: String(pickDispatchField(row, "buyerIntervention", "BuyerIntervention") ?? ""),
    BuyerComments: row.BuyerComments ?? row.buyerComments ?? null,
    poNumber: pickDispatchField(row, "poNumber", "po_number"),
    poLineItem: pickDispatchField(row, "poLineItem", "po_line_item"),
    dispatchStatus,
  };
}

/**
 * Build the `/api/RMPMShortage` payload that narrows the RMPM alert list down to the
 * alert behind a dispatch row. `filterData` keys must match RMPM filter labels.
 */
export function buildRmpmAlertLookupPayload(alertDetails, { email, role } = {}) {
  const weekDigits = String(alertDetails?.Week ?? "").replace(/\D/g, "");
  const filterValues = {
    Week: weekDigits ? `Week ${weekDigits}` : "",
    "Site code": alertDetails?.["Site code"],
    "Material Id": alertDetails?.["Material Id"],
  };
  const filterData = Object.entries(filterValues).reduce((acc, [label, value]) => {
    if (value != null && String(value).trim() !== "") {
      acc[label] = [String(value).trim()];
    }
    return acc;
  }, {});

  return {
    email,
    type: "RMPM Alert",
    role,
    page: 1,
    per_page: 25,
    clearFilters: false,
    filterData,
    sortBy: "",
    sortOrder: "asc",
    week: null,
  };
}
